import type {
  ExpandedRunGraphCard,
  ExpandedRunGraphEdge,
} from './workflow-run-graph-fan-out';
import { makeRunFlowNodeId } from './workflow-run-graph-fan-out';

/**
 * Synthetic instance key for a collapsed fan-out group card. Real instance keys
 * come from step ids, so this never collides; it only has to be stable so the
 * group's React Flow id (`defId::__group__`) stays put across re-layouts.
 */
export const FAN_OUT_GROUP_INSTANCE_KEY = '__group__';

/** One status bucket in a fan-out rollup, e.g. `{ status: 'completed', count: 298 }`. */
export type FanOutStatusCount = {
  status: string;
  count: number;
};

/**
 * The summary a collapsed fan-out card carries instead of N side-by-side cards:
 * how many iterations ran, the per-status rollup (for the triage bar), and a
 * single representative status (for edge progress painting downstream).
 */
export type FanOutGroupSummary = {
  /** Number of fanned iterations this card stands in for. */
  count: number;
  /** Per-status counts, ordered for display (failures surfaced first). */
  statusCounts: FanOutStatusCount[];
  /** Rolled-up status driving the downstream edge paint, or null when unknown. */
  rollupStatus: string | null;
};

/** An expanded card plus (when it collapses a fan-out) its group summary. */
export type CollapsedRunGraphCard = ExpandedRunGraphCard & {
  fanOut: FanOutGroupSummary | null;
};

const ACTIVE_STATUSES = new Set(['queued', 'incomplete', 'running']);
const FAILURE_STATUSES = new Set(['failed', 'errored']);

/**
 * Display order for the rollup: failures first (triage), then in-flight, then
 * success, then benign-terminal. Statuses outside this list sort last, stably.
 */
const STATUS_DISPLAY_ORDER = [
  'failed',
  'errored',
  'running',
  'incomplete',
  'queued',
  'completed',
  'cancelled',
  'stopped',
  'absorbed',
];

function statusOrderIndex(status: string): number {
  const index = STATUS_DISPLAY_ORDER.indexOf(status);
  return index === -1 ? STATUS_DISPLAY_ORDER.length : index;
}

/**
 * Folds a set of iteration statuses into ordered counts plus one rolled-up
 * status: still going if any iteration is active, else failed if any failed,
 * else completed. Benign-terminal statuses (cancelled/stopped/absorbed) roll up
 * to completed, matching how the run itself rolls step statuses up.
 */
export function summarizeFanOutStatuses(
  statuses: readonly (string | null)[]
): FanOutGroupSummary {
  const counts = new Map<string, number>();
  for (const status of statuses) {
    const key = status ?? 'unknown';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const statusCounts: FanOutStatusCount[] = [...counts.entries()]
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => {
      const byOrder = statusOrderIndex(a.status) - statusOrderIndex(b.status);
      return byOrder !== 0 ? byOrder : a.status.localeCompare(b.status);
    });

  const hasActive = statuses.some(
    (status) => status !== null && ACTIVE_STATUSES.has(status)
  );
  const hasFailure = statuses.some(
    (status) => status !== null && FAILURE_STATUSES.has(status)
  );
  const rollupStatus =
    statuses.length === 0
      ? null
      : hasActive
      ? 'incomplete'
      : hasFailure
      ? 'failed'
      : 'completed';

  return { count: statuses.length, statusCounts, rollupStatus };
}

/**
 * Collapses each fanned-out definition node (its N instance cards) into a single
 * "group" card carrying a {@link FanOutGroupSummary}, and rewrites the expanded
 * edges onto those group cards (deduped).
 *
 * This keeps the run graph O(1) in width regardless of iteration count: a
 * for-each over 300 items renders as one stacked card with a 298/2 rollup rather
 * than 300 cards strung across the canvas. Per-iteration detail is reached by
 * clicking the card (the step view already lists every instance). This mirrors
 * how Step Functions (Map Run), Airflow (dynamic task mapping) and Camunda
 * render fan-out — collapse + rollup + drill-in — and avoids the per-iteration
 * node explosion that makes other tools' run views unusable at scale.
 *
 * Non-fanned cards pass through unchanged with `fanOut: null`.
 *
 * Grouping is **per definition node**, not per `(definitionNodeId,
 * parentInstanceKey)`: every instance of a node folds into one card regardless
 * of which parent iteration spawned it. This is intentional — the goal is one
 * card per step no matter the nesting, so a node nested under an outer loop does
 * not re-fan into one card per outer iteration (which would reintroduce the
 * width blow-up this collapse exists to remove). The for-each used on the canvas
 * today is single-level, so every body instance already shares one parent; the
 * per-definition rule only matters for future nesting, where collapsing fully is
 * still the behaviour we want.
 */
export function collapseFanOutGroups(
  cards: readonly ExpandedRunGraphCard[],
  edges: readonly ExpandedRunGraphEdge[]
): { cards: CollapsedRunGraphCard[]; edges: ExpandedRunGraphEdge[] } {
  const cardsByDefinition = new Map<string, ExpandedRunGraphCard[]>();
  for (const card of cards) {
    const group = cardsByDefinition.get(card.definitionNodeId) ?? [];
    group.push(card);
    cardsByDefinition.set(card.definitionNodeId, group);
  }

  const collapsedCards: CollapsedRunGraphCard[] = [];
  // Maps each original (fanned) card flowId to the group flowId it folds into,
  // so edges can be redirected onto the single group card.
  const flowIdRemap = new Map<string, string>();

  for (const card of cards) {
    const siblings = cardsByDefinition.get(card.definitionNodeId) ?? [card];
    const fanned = siblings.length > 1;
    if (!fanned) {
      collapsedCards.push({ ...card, fanOut: null });
      continue;
    }

    const groupFlowId = makeRunFlowNodeId(
      card.definitionNodeId,
      FAN_OUT_GROUP_INSTANCE_KEY
    );
    flowIdRemap.set(card.flowId, groupFlowId);

    // Emit the group card once, from the first sibling (stable order).
    if (siblings[0] !== card) {
      continue;
    }
    const summary = summarizeFanOutStatuses(siblings.map((s) => s.status));
    collapsedCards.push({
      ...card,
      flowId: groupFlowId,
      instanceKey: FAN_OUT_GROUP_INSTANCE_KEY,
      status: summary.rollupStatus,
      error: null,
      itemIndex: null,
      fanOut: summary,
    });
  }

  const remap = (flowId: string): string => flowIdRemap.get(flowId) ?? flowId;
  const seen = new Set<string>();
  const collapsedEdges: ExpandedRunGraphEdge[] = [];
  for (const edge of edges) {
    const fromFlowId = remap(edge.fromFlowId);
    const toFlowId = remap(edge.toFlowId);
    if (fromFlowId === toFlowId) {
      // A within-group edge (shouldn't occur across definition nodes) would be a
      // self-loop once collapsed — drop it.
      continue;
    }
    const key = `${fromFlowId}=>${toFlowId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    collapsedEdges.push({ id: key, fromFlowId, toFlowId });
  }

  return { cards: collapsedCards, edges: collapsedEdges };
}
